import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Tags, Search, Loader2, FolderOpen } from 'lucide-react';
import { categoriasService } from '@/services/productos.service';
import { useAuthStore } from '@/stores/auth-store';
import { useApiError } from '@/hooks/useApiError';
import { dialogs } from '@/stores/dialog-store';

interface Category {
  id: string;
  name: string;
  description?: string;
  parentId: string | null;
  parent?: { name: string };
  _count?: { products: number };
}

export function CategoriesPage() {
  const { user } = useAuthStore();
  const { handleAuthRedirect } = useApiError();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newParentId, setNewParentId] = useState('');
  const [saving, setSaving] = useState(false);

  // Edit modal
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editParentId, setEditParentId] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  const loadCategories = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await categoriasService.findAll(user?.company?.id || '');
      setCategories(data || []);
    } catch (err: any) {
      if (err?.response?.status === 401) { handleAuthRedirect(); return; }
      setError(err.response?.data?.message || 'Error al cargar categorías');
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.company?.id) loadCategories();
  }, [user?.company?.id]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      await categoriasService.create({
        companyId: user?.company?.id,
        name: newName.trim(),
        description: newDescription.trim() || undefined,
        parentId: newParentId || undefined,
      });
      setNewName('');
      setNewDescription('');
      setNewParentId('');
      setShowForm(false);
      loadCategories();
    } catch {
      await dialogs.alert('Error al crear categoría');
    } finally {
      setSaving(false);
    }
  };

  const openEditModal = (cat: Category) => {
    setEditingCategory(cat);
    setEditName(cat.name);
    setEditDescription(cat.description || '');
    setEditParentId(cat.parentId || '');
    setEditSaving(false);
  };

  const handleEditSubmit = async () => {
    if (!editingCategory || !editName.trim()) return;
    setEditSaving(true);
    try {
      await categoriasService.update(editingCategory.id, {
        name: editName.trim(),
        description: editDescription.trim() || undefined,
        parentId: editParentId || null,
      });
      setEditingCategory(null);
      loadCategories();
    } catch {
      await dialogs.alert('Error al actualizar categoría');
    } finally {
      setEditSaving(false);
    }
  };

  const handleDeactivate = async (id: string, name: string) => {
    if (!(await dialogs.confirm(`¿Desactivar categoría "${name}"?`, { destructive: true }))) return;
    try {
      await categoriasService.deactivate(id);
      loadCategories();
    } catch {
      await dialogs.alert('Error al desactivar categoría');
    }
  };

  const filtered = categories.filter((c) =>
    !search || c.name.toLowerCase().includes(search.toLowerCase()),
  );

  const topLevel = categories.filter((c) => !c.parentId);

  return (
    <div className="w-full space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Categorías</h1>
          <p className="text-sm text-ios-gray mt-1">{categories.length} categorías registradas</p>
        </div>
        <button onClick={() => setShowForm(!showForm)} className="ios-button flex items-center gap-2">
          {showForm ? <></> : <Plus size={18} />}
          {showForm ? 'Cancelar' : 'Nueva Categoría'}
        </button>
      </div>

      {/* Create Form */}
      {showForm && (
        <div className="ios-card p-4 space-y-3">
          <div className="flex gap-3">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Nombre de la categoría..."
              className="ios-input flex-1"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            />
            <input
              type="text"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              placeholder="Descripción (opcional)..."
              className="ios-input w-64"
            />
            <select
              value={newParentId}
              onChange={(e) => setNewParentId(e.target.value)}
              className="ios-input w-56"
            >
              <option value="">— Categoría principal —</option>
              {topLevel.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
            <button
              onClick={handleCreate}
              disabled={saving || !newName.trim()}
              className="ios-button disabled:opacity-50"
            >
              {saving ? 'Guardando...' : 'Crear'}
            </button>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ios-gray" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar categoría..."
          className="ios-input w-full pl-9 h-9 text-sm"
        />
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 text-ios-red text-sm p-4 rounded-[12px] border border-red-100">{error}</div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 size={24} className="animate-spin text-ios-gray" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="ios-card p-12 text-center">
          <Tags size={48} className="mx-auto text-ios-gray-4 mb-4" />
          <h3 className="text-lg font-medium text-ios-gray mb-1">
            {search ? 'Sin resultados' : 'No hay categorías'}
          </h3>
          <p className="text-sm text-ios-gray mb-4">
            {search ? 'No se encontraron categorías con ese término' : 'Comienza agregando tu primera categoría'}
          </p>
        </div>
      ) : (
        <div className="ios-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ios-gray-5 bg-ios-gray-6/50">
                  <th className="text-left px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Categoría</th>
                  <th className="text-left px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Descripción</th>
                  <th className="text-left px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Categoría Padre</th>
                  <th className="text-right px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Productos</th>
                  <th className="text-right px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ios-gray-5">
                {filtered.map((cat) => (
                  <tr key={cat.id} className="hover:bg-ios-gray-6/30 transition-colors">
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-gradient-to-br from-violet-500/20 to-purple-200 rounded-[10px] flex items-center justify-center flex-shrink-0">
                          {cat.parentId ? (
                            <FolderOpen size={16} className="text-violet-600" />
                          ) : (
                            <Tags size={16} className="text-purple-600" />
                          )}
                        </div>
                        <span className="font-medium text-ios-dark">{cat.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-ios-gray text-xs">
                      {cat.description || <span className="text-ios-gray-4">—</span>}
                    </td>
                    <td className="px-4 py-3.5 text-xs text-ios-gray">
                      {cat.parent?.name ? (
                        <span className="inline-flex items-center gap-1">
                          <FolderOpen size={11} className="text-violet-400" />
                          {cat.parent.name}
                        </span>
                      ) : (
                        <span className="text-ios-gray-4">Principal</span>
                      )}
                    </td>
                    <td className="px-4 py-3.5 text-right text-sm font-medium">
                      {cat._count?.products || 0}
                    </td>
                    <td className="px-4 py-3.5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEditModal(cat)}
                          className="p-1.5 rounded-lg hover:bg-blue-50 text-ios-gray hover:text-ios-blue transition-colors"
                          title="Editar"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={() => handleDeactivate(cat.id, cat.name)}
                          className="p-1.5 rounded-lg hover:bg-red-50 text-ios-gray hover:text-ios-red transition-colors"
                          title="Desactivar"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Edit Modal ── */}
      {editingCategory && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setEditingCategory(null)}>
          <div className="bg-white rounded-[20px] w-full max-w-md shadow-ios-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-ios-gray-5">
              <h3 className="font-semibold">Editar Categoría</h3>
              <button onClick={() => setEditingCategory(null)} className="p-2 rounded-lg hover:bg-ios-gray-6">
                <span className="text-lg leading-none">×</span>
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="text-xs text-ios-gray mb-1 block font-medium">Nombre *</label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder="Nombre de la categoría"
                  className="ios-input w-full"
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && handleEditSubmit()}
                />
              </div>
              <div>
                <label className="text-xs text-ios-gray mb-1 block font-medium">Descripción</label>
                <input
                  type="text"
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  placeholder="Descripción opcional"
                  className="ios-input w-full"
                />
              </div>
              <div>
                <label className="text-xs text-ios-gray mb-1 block font-medium">Categoría Padre</label>
                <select
                  value={editParentId}
                  onChange={(e) => setEditParentId(e.target.value)}
                  className="ios-input w-full"
                >
                  <option value="">— Principal —</option>
                  {topLevel
                    .filter((c) => c.id !== editingCategory.id)
                    .map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                </select>
              </div>
            </div>
            <div className="p-4 border-t border-ios-gray-5 flex gap-2 justify-end">
              <button
                onClick={() => setEditingCategory(null)}
                className="px-4 py-2.5 rounded-[10px] text-sm border border-ios-gray-5 hover:bg-ios-gray-6"
              >
                Cancelar
              </button>
              <button
                onClick={handleEditSubmit}
                disabled={editSaving || !editName.trim()}
                className="px-4 py-2.5 rounded-[10px] text-sm bg-ios-blue text-white font-medium hover:bg-ios-blue-600 disabled:opacity-50"
              >
                {editSaving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
